import ResponderMap from 'features/Responder/features/ResponderMap';
import { usePersonnel } from 'features/Responder/features/Personnel/contexts/PersonnelContext';
import { focusMapOnGeometry } from 'features/Responder/utils/focusMapOnGeometry';
import { pickLatestPersonnelLocation } from 'features/Responder/utils/personnelLocation';
import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';

const SelectedPersonnelFocus = ({ selectedPersonnelId, focusNonce = 0 }) => {
  const map = useMap();
  const { personnel } = usePersonnel();
  const lastFocusKeyRef = useRef(null);

  const target = useMemo(() => {
    if (selectedPersonnelId == null || !personnel) return null;
    const values = Object.values(personnel);
    const person =
      values.find((entry) => `${entry?.userId ?? entry?.id}` === `${selectedPersonnelId}`) || null;
    return pickLatestPersonnelLocation(person)?.latLng ?? null;
  }, [personnel, selectedPersonnelId]);

  useEffect(() => {
    if (!map || !target) return;
    const focusKey = `${selectedPersonnelId ?? 'none'}:${focusNonce}:${target[0]}:${target[1]}`;
    if (lastFocusKeyRef.current === focusKey) return;

    focusMapOnGeometry(map, [target, target], {
      padding: [80, 80],
      maxZoom: 16,
      animate: true,
    });
    lastFocusKeyRef.current = focusKey;
  }, [focusNonce, map, selectedPersonnelId, target]);

  return null;
};

const PersonnelMap = ({ selectedPersonnelId = null, focusNonce = 0 }) => {
  const mapOverlays =
    selectedPersonnelId != null ? (
      <SelectedPersonnelFocus selectedPersonnelId={selectedPersonnelId} focusNonce={focusNonce} />
    ) : null;

  return (
    <div className="d-flex flex-column h-100 p-3 rounded-2">
      <h4>Personnel Map</h4>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <ResponderMap
          editable={false}
          enableZones={false}
          enableTools={false}
          enableTourTools={false}
          enableContextMenu={false}
          autoSelectZones={false}
          showPersonnel={true}
          showGoogleLocationSearch={true}
          showResetZoomControl={true}
          enableInitialUserLocation={false}
          fallbackMapCenter={[39.8283, -98.5795]}
          initialMapZoom={4}
          extraLayers={mapOverlays}
        />
      </div>
    </div>
  );
};

export default PersonnelMap;
